<!-- 
  Vue3 Otp Input v0.3.6 component for per-character input.
  Modified so changes to external props can be reflected internally.

  Also has a hidden <a-input> for form validation with ant design.

  @see https://github.com/ejirocodes/vue3-otp-input/tree/v0.3.6
  -->
<template>
  <div class="basic-otp-input" ref="basicOtpInputRef">
    <slot name="prefix" />

    <!--
      Hidden input for validation only 
      Important: Placed here before v-otp-input for CSS selector ~
    -->
    <a-input
      autocomplete="off"
      type="hidden"
      :maxlength="max"
      v-model:value="innerValue"
      v-bind="$attrs"
      ref="basicOtpInputElement"
      autocorrect="disabled"
      autocapitalize="disabled"
      spellcheck="false"
      :inputmode="!!numericOnly ? 'numeric' : 'text'"
      :pattern="!!numericOnly ? regex.otpNumericOnly : regex.otpNonNumeric"
      @keypress="allowLatinSymbolsNumbers($event)"
      @drop="allowLatinSymbolsNumbers($event)"
      @paste="allowLatinSymbolsNumbers($event)"
      :disabled="loading"
    />

    <v-otp-input
      ref="vOtpInputRef"
      class="basic-otp-input__wrapper"
      input-classes="basic-otp-input__input-slot"
      :num-inputs="max"
      :should-auto-focus="true"
      :input-type="!!numericOnly ? 'number' : 'letter-numeric'"
      :inputmode="!!numericOnly ? 'numeric' : 'text'"
      separator=""
      autocomplete="one-time-code"
      :is-disabled="loading"
      @on-change="handleInternalOnChange"
      @paste="handleInternalOnPaste"
      @click="handleClick"
    />

    <slot name="suffix" />

    <slot name="addonAfter" />
  </div>

  <div class="basic-otp-input-extra">
    <!-- Demo text with code suggestion -->
    <a-typography-paragraph v-if="showDemoText" class="mt-16">
      {{ t("For DEMO enter 11 11 11 to continue") }}
    </a-typography-paragraph>
  </div>
</template>

<style lang="scss">
.basic-otp-input {
  display: flex;
  align-items: center;
  gap: 5px;

  & &__wrapper {
    display: inline-flex !important; // override default by vue3-otp-input
    gap: $padding-xss;
    max-width: 360px;
    margin: auto;
    // digit will not be hidden especially on safari mobile
    padding: 0px;
    transition: all 0.3s ease;

    > div {
      flex: 1;
      min-width: 0px;
    }
  }

  // Per-character <input>
  & &__input-slot {
    font-size: $basic-otp-input-font-size;
    text-align: center;
    background-color: transparent;
    padding: 0px;
    width: 100%;
    line-height: 1;
    // Border will not be round especially on Safari mobile
    border-radius: unset;

    // Border
    border: none;
    border-bottom: $basic-otp-input-underline-thickness solid
      $basic-otp-input-underline-color;

    transition: all 0.3s ease;

    &:focus {
      border-bottom: $basic-otp-input-underline-thickness solid $accent;
    }
    &:focus-visible {
      outline: none;
    }
  }

  // State: Success
  &.success {
    .basic-otp-input__input-slot {
      border-color: $success-color;
      color: $success-color;
    }
  }

  // State: Error
  &.error {
    .basic-otp-input__input-slot {
      border-color: $error-color;
      color: $error-color;
    }
  }

  // State: Disabled
  .ant-input.ant-input-disabled
    ~ .basic-otp-input__wrapper
    .basic-otp-input__input-slot {
    color: $input-disabled-color;
    cursor: not-allowed;
  }
}

// Countdown styles
.basic-otp-input-extra {
  .ant-statistic-content {
    margin-top: $margin-lg;
    color: $text-color-less;
    text-align: center;
  }
}
</style>

<script lang="ts">
import {
  defineComponent,
  ref,
  watch,
  nextTick,
  watchEffect,
  onMounted,
  computed,
} from "vue";
import { useI18n } from "vue-i18n";
import { allowLatinSymbolsNumbers } from "@/shared/utils/inputs";
import noop from "lodash/noop";
import { regex } from "@/shared/utils/constants";
import VOtpInput from "vue3-otp-input";

/**
 * Component for OTP Input
 *
 * Features:
 *  - Custom Styling
 *  - Resend functionality with timeout
 */
export default defineComponent({
  props: {
    /**
     * Value should always be `String`, so OTPs that start with 0,
     * are not simplified or converted to other base.
     *
     * Ex. `Number(0123)` is not equal to `Number(123)`
     */
    value: {
      type: String,
      required: true,
    },
    /**
     * TODO: Add .env variable for demo otp
     * If set to true, ths will show demo message with demo code suggestion
     */
    showDemoText: {
      type: Boolean,
      default: false, // for now make this false by default
    },
    /**
     * If set to true, this can mean that the OTP is resending or the form is loading
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * added numeric only as props with default value of true
     * for mobile document short code purposes
     */
    numericOnly: {
      type: Boolean,
      default: true,
    },
    /**
     * If enabled, on v-model:value changed will trigger inner value update
     * Useful in cases where input value is updated programmatically
     */
    updateOnPropsValueChanged: {
      type: Boolean,
      default: false,
    },
    //passed by phone number to only allow 4 max value
    max: {
      type: Number,
      default: 6,
    },
    capitalize: {
      type: Boolean,
      default: false,
    },
    /**
     * If true - will allow other input like Cyrillic, Arabic, etc
     */
    allowNonLatin: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:value", "on-resend-click", "on-complete"],
  setup(props, { emit }) {
    const formatOtp = (code: string): string => {
      // Ensures no spaces, within max bounds,
      return code.replaceAll(" ", "").substring(0, props.max);
    };

    const t = useI18n().t;
    const basicOtpInputRef = ref<HTMLElement>();
    const innerValue = ref(props.value);

    /** NOTE: This is to update the 'innerValue' properly when
     * prop 'value' changes */
    watch(
      () => props.value,
      (newValue) => {
        /**
         * check if updateOnPropsValueChanged is true
         * there's an issue on cursor position resetting when value is updated here
         */
        if (props.updateOnPropsValueChanged) {
          // Avoids redundant update due to
          // 1. innerValue emitting a change to external prop
          // 2. external prop, then re-emits the change in this watch
          if (newValue === innerValue.value) {
            return;
          }

          // Assigns a blank value if `null` or `undefined`
          const value = newValue?.toString() ?? "";
          innerValue.value = formatOtp(value);

          updateVOtpInput(innerValue.value);
        }
      }
    );

    // === v-otp-input ===
    const vOtpInputRef = ref();
    const vOtpInternalValue = computed(
      () => vOtpInputRef.value.otp.join("") ?? ""
    );

    /** Only force update capitalization if can accept letters and capitalize is true  */
    const shouldUpdateCapitalization = computed<boolean>(
      () => props.capitalize && !props.numericOnly
    );

    // Helpers
    const updateSingleVOtpInput = (character: string, index: number) => {
      nextTick(() => {
        vOtpInputRef.value.handleOnFocus(index);
        vOtpInputRef.value.handleOnChange(character.charAt(0));
      });
    };

    // Handlers
    const handleInternalOnChange = (value: string) => {
      const formattedValue = formatOtp(value);

      innerValue.value = formattedValue;
      emit("update:value", formattedValue);

      if (formattedValue.length >= props.max) {
        emit("on-complete", formattedValue);
      }

      resetState();
    };

    const handleInternalOnPaste = () => {
      handleInternalOnChange(
        shouldUpdateCapitalization.value
          ? vOtpInternalValue.value.toUpperCase()
          : vOtpInternalValue.value
      );

      // Manually update inputs
      if (shouldUpdateCapitalization.value) {
        updateVOtpInput(vOtpInternalValue.value);
      }
    };

    /**
     * Manually update v-otp-input <input> elements because it does not support
     * programmatically update using v-model
     */
    const updateVOtpInput = (value: string) => {
      if (!value) return;

      // Loop over each character
      vOtpInputRef.value.handleOnFocus(0);
      for (let i = 0; i < value.length; i++) {
        const character = value.charAt(i);

        // Paste internally vue3-otp-input
        vOtpInputRef.value.handleOnChange(
          props.capitalize ? character.toUpperCase() : character
        );
      }
    };

    const resetState = () => {
      basicOtpInputRef.value?.classList.remove("success", "error");
    };

    /**
     * separated the click handler to avoid calling the handleOnFocus on change of value
     * since the resetState is being called on change of input value
     */
    const handleClick = () => {
      // pass the index focus only if the otp input is empty
      if (!innerValue.value) vOtpInputRef.value.handleOnFocus(0);

      resetState();
    };

    watchEffect(() => {
      // Temporary fix for this issue https://github.com/ejirocodes/vue3-otp-input/issues/27
      nextTick(() => {
        if (!basicOtpInputRef.value || !vOtpInputRef.value) return;

        const inputElements =
          basicOtpInputRef.value.querySelectorAll<HTMLInputElement>(
            ".basic-otp-input__input-slot"
          );

        inputElements.forEach((el) => {
          el.pattern = props.numericOnly
            ? regex.otpNumericOnly
            : regex.otpNonNumeric;
        });

        inputElements.forEach((element, index) => {
          element.addEventListener("input", (event) => {
            const inputElement = event.target as HTMLInputElement;
            const character = inputElement.value.toUpperCase();

            if (shouldUpdateCapitalization.value) {
              updateSingleVOtpInput(character, index);
            }
          });
        });
      });
    });

    onMounted(() => {
      // To update component's default `props.value`
      updateVOtpInput(innerValue.value);
    });

    // Focuses on the last input value index
    const handleFocusLastIndex = () => {
      if (!basicOtpInputRef.value || !vOtpInputRef.value) return;

      const inputElements =
        basicOtpInputRef.value.querySelectorAll<HTMLInputElement>(
          ".basic-otp-input__input-slot"
        );
      // Fetch the last index of OTP
      const innerValueLastIndex = innerValue.value?.length - 1;

      // For each element of input elements
      inputElements?.forEach((element, index) => {
        // If current looped index is equals to the inner value last index
        if (index == innerValueLastIndex) {
          // Focus and select the last input
          element.focus();
          element.select();

          // for the cursor to show, we need to set the input type and mode to text
          element.type = "text";
          element.inputMode = "text";
          // Set the cursor range
          element.setSelectionRange(1, element.value.length);

          // Reset back to its original state if the OTP is numeric
          if (props.numericOnly) {
            element.type = "number";
            element.inputMode = "numeric";
          }
        }
      });
    };

    return {
      t,
      basicOtpInputRef,
      innerValue,
      basicOtpInputElement: basicOtpInputRef,
      allowLatinSymbolsNumbers: props.allowNonLatin
        ? noop
        : allowLatinSymbolsNumbers,
      regex,
      // v-otp-input
      vOtpInputRef,
      handleInternalOnChange,
      handleInternalOnPaste,
      resetState,
      handleClick,
      handleFocusLastIndex,
    };
  },
  components: {
    VOtpInput,
  },
});
</script>
